// Portfolio Tax Trend — year-over-year assessed value and tax, one row per PROPERTY per year.
//
// The other two parcel-grained reports answer "what happened to this account". This one answers
// "which way is the portfolio moving", so it rolls the shared parcel-year scope up to the property
// and computes the change between years.
//
// ═══ THE GAP RULE — the decision this report turns on ═══
// Some years have values but no bills, so the tax figure is genuinely absent. A blank must never
// become a 0% change or a -100% drop, and it must not silently poison the following year either.
//
// ⛔ CHOSE TO BRIDGE THE GAP, AND TO NAME WHAT WAS BRIDGED. A year compares against the most recent
// EARLIER year that actually has a figure, and the "vs Yr" column says which year that was. The
// alternative — blanking any year whose immediate predecessor is missing — loses the trend across
// a gap, which is the one thing this report exists to show. Bridging without saying so would be
// worse than either: a reader would assume year-on-year and silently misread a two-year movement
// as one. So the comparison year is a VISIBLE COLUMN, not a footnote and not an implication.
//
// Blank stays blank: no figure this year, or no earlier figure to compare against, and both the
// absolute and the percent cells are empty. A percent against a zero base reads n/m (below) — an
// infinite change is not a number a client can read.
//
// ═══ n/m — A PERCENTAGE ON A PLACEHOLDER BASE (Philip, 2026-10-10) ═══
// Orlando Airport 2024 vs 2023 printed Δ Assessed % 800,708.67% and Δ Taxes % 803,080.11%: 2023 was a leasehold
// assessed at a nominal $100 a parcel ($300, $5.43 tax). Correct arithmetic, meaningless figure. A percent reads
// 'n/m' (format.js NOT_MEANINGFUL) when the prior figure is under its floor — assessed $10,000, tax $100; a zero
// base is under the floor too — or the change is beyond ±500%. The cell's note says which, on hover. The Δ $
// columns are untouched: they are the honest figure in exactly these cases.
//
// ═══ ⛔ THE Δ ASSUMES A STABLE BASKET ═══
// Each year's Assessed figure is the sum over WHICHEVER PARCELS CARRIED A VALUE THAT YEAR, and the
// Δ subtracts one such sum from another. That is only a trend if the two years hold the same
// parcels. When the count moves materially the Δ measures the BASKET, not the market, and it will
// say so in the most alarming way available: 18 East has values for 27 parcels in 2021 and 2022
// and for ONE in 2023, which renders as a -90.8% collapse. Nothing lost 90% of its value; 26
// parcels simply have no 2023 notice yet. The `Accounts` column (optional, off by default) shows the count, and
// `Parcel changes` (below) names them: "26 accounts not in 2023".
//
// Like-for-like Δ columns — comparing only parcels present in BOTH years, with the overlap count
// shown — were considered and DELIBERATELY NOT BUILT (2026-08-25). On this portfolio 2021→2022 is
// the identical 27 parcels, so an "LfL Accounts" column would read "27 of 27" on every row: noise
// inviting a question with no interesting answer. The remaining swing is a DATA GAP, not churn,
// and it resolves when the missing notices are loaded.
//
// ⛔ IF A PORTFOLIO WITH GENUINE CHURN ARRIVES — parcels really entering and leaving between years
// — this is where to start. The change is: compute each Δ over the intersection of parcels holding
// a figure in both years, head the columns "(LfL)", and surface the overlap count so a narrow
// comparison is visible. Accept that Assessed(y) − Assessed(y−1) will then no longer equal the Δ,
// because they measure different baskets; that is exactly why the header has to say so.
//
// ⛔ PARCEL CHANGES (Philip, 2026-10-10) — the half of that which is cheap and always honest: the row SAYS when its
// parcel set differs from the year before it (the property's previous row), so the reader knows the years are not
// like-for-like. Each parcel that came or went is named with why, read from its representation span:
//   retired      — not ours this year (retired_year reached, not reactivated)
//   reactivated  — ours again this year (reactivated_year in (prior year, this year])
//   added        — first represented this year (first_year in (prior year, this year])
//   not in YYYY  — still ours both years, but with no figures in one of them (a data gap, e.g. 18 East 2023)
// More than three of one kind collapse to a count, so a 26-parcel data gap is one phrase, not a paragraph.

import { fetchParcelYearScope, buildParcelYearRows } from '../parcelYearRows';
import { toNumber, sumColumn, isEstimated, NOT_MEANINGFUL, NM_MIN_ASSESSED, NM_MIN_TAX, NM_MAX_PCT } from '../format';
import { retiredAsOf } from '../../utils/retirement';
import { groupKey, assertRowFields } from '../grouping';

// Wrap a computed figure so it inherits the estimate flag from whatever it was derived from —
// the same rule sumColumn applies, extended to the derived YoY cells.
const flag = (value, ...sources) => {
  if (value == null) return null;
  return sources.some((s) => isEstimated(s)) ? { value, estimated: true } : value;
};

const dollars = (n, dp) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

// The percent cell: a number, or n/m with the reason. `what` names the figure ('assessed' / 'tax'), `deltaCol` the
// column holding the dollar change, `dp` how its dollars print.
export function percentChange(cur, prev, { floor, what, deltaCol, dp, prevYear }) {
  if (cur == null || prev == null) return null;
  const nm = (why) => ({ value: NOT_MEANINGFUL, note: `Not meaningful: ${why}. ${deltaCol} shows the change in dollars.` });
  if (Math.abs(prev) < floor) {
    return nm(`${prevYear} ${what} was ${dollars(prev, dp)}, under ${dollars(floor, 0)} — too small a base for a percentage`);
  }
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  if (Math.abs(pct) > NM_MAX_PCT) {
    return nm(`the change is ${pct > 0 ? '+' : ''}${pct.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%, beyond ±${NM_MAX_PCT}%`);
  }
  return pct;
}

// "03-24-30-0000-10-004 retired; 1234 added" — the parcels that differ between the property's previous row and
// this one, and why (see PARCEL CHANGES above). '' when the set is the same or there is no previous row.
export function parcelChanges(prevRows, curRows, prevYear, year) {
  if (!prevRows) return '';
  const byId = (rows) => new Map(rows.map((r) => [r.parcel_id, r]));
  const prev = byId(prevRows); const cur = byId(curRows);
  const within = (y) => y != null && String(y) > String(prevYear) && String(y) <= String(year);
  const kinds = new Map();
  const add = (kind, r) => { const l = kinds.get(kind) || []; l.push(r.account_number || r.parcel_id); kinds.set(kind, l); };
  prev.forEach((r, id) => {
    if (cur.has(id)) return;
    add(retiredAsOf(r.parcel_retired_year, r.parcel_reactivated_year, year) ? 'retired' : `not in ${year}`, r);
  });
  cur.forEach((r, id) => {
    if (prev.has(id)) return;
    add(within(r.parcel_reactivated_year) ? 'reactivated' : within(r.parcel_first_year) ? 'added' : `not in ${prevYear}`, r);
  });
  return [...kinds].map(([kind, accts]) => (accts.length > 3
    ? `${accts.length} accounts ${kind}`
    : `${accts.sort().join(', ')} ${kind}`)).join('; ');
}

const asOfLabel = (d = new Date()) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const titleRows = (clientName, heading) => [null, clientName || '', heading, `As of ${asOfLabel()}`, null, null];

export const portfolioTaxTrend = {
  id: 'portfolio_trend',
  title: 'Portfolio Tax Trend',
  description: 'Year-over-year assessed value and tax across the portfolio, by property.',

  // ⛔ ALL YEARS, DELIBERATELY, AND THE ONLY REPORT THAT OPTS OUT. Every other report defaults to the
  // current value year (ReportView seeds it) because that is what someone wants most of the time. This one
  // computes year-over-year deltas against the previous year, so one selected year is a trend of a single
  // point — broken rather than tidier. Consistency of defaults would be the wrong target here.
  yearsDefault: 'all',
  filters: [
    'client',
    'years',
    { key: 'basis', type: 'toggle', label: 'Basis', default: 'taxable',
      options: [{ value: 'taxable', label: 'Taxable' }, { value: 'market', label: 'Market' }] },
    { key: 'state', type: 'enum', label: 'State', from: 'state' },
    { key: 'property', type: 'enum', label: 'Property', from: 'property', picker: 'search', noun: 'properties' },
  ],

  columns: [
    // Locked: a row here is a property-year, so Year and Property identify it (there is no Account #).
    { key: 'tax_year', label: 'Year', type: 'year', locked: true },
    { key: 'property', label: 'Property', type: 'text', locked: true },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'assessed', label: 'Assessed', type: 'value', total: true },
    { key: 'assessed_delta', label: 'Δ Assessed', type: 'value' },
    { key: 'assessed_pct', label: 'Δ Assessed %', type: 'percent' },
    { key: 'taxes', label: 'Taxes', type: 'money', total: true },
    { key: 'taxes_delta', label: 'Δ Taxes', type: 'money' },
    { key: 'taxes_pct', label: 'Δ Taxes %', type: 'percent' },
    // Shown by default: the gap rule is only honest if the comparison year is on the page. Hideable since
    // 2026-10-10 like every column but the identifying ones (Philip).
    { key: 'vs_year', label: 'vs Yr', type: 'text' },
    { key: 'parcel_changes', label: 'Parcel changes', type: 'text' },
    { key: 'parcels', label: 'Accounts', type: 'int', optional: true, total: true },
  ],

  fetch: (ctx) => fetchParcelYearScope(ctx, 'portfolio trend'),

  buildRows(raw, ctx) {
    const parcelRows = buildParcelYearRows(raw, ctx);
    if (!parcelRows.length) return [];
    // ⛔ The fields this report GROUPS on must exist on the rows it was handed. property_id did
    // not, and every parcel in the client silently keyed to "undefined|2021".
    assertRowFields(parcelRows, ['property_id', 'tax_year', 'final_value', 'taxes'], 'portfolio trend');

    // ── roll parcel-years up to property-years ────────────────────────────────────────────────
    const byPropYear = new Map();
    parcelRows.forEach((r) => {
      const k = groupKey(r, ['property_id', 'tax_year'], 'portfolio trend');
      const g = byPropYear.get(k) || {
        property_id: r.property_id, client_id: r.client_id, property: r.property, state: r.state,
        tax_year: String(r.tax_year), rows: [],
      };
      g.rows.push(r);
      byPropYear.set(k, g);
    });

    const groups = [...byPropYear.values()].map((g) => ({
      ...g,
      // ⛔ ASSESSED IS final_value — THE VALUE IN EFFECT, i.e. the deepest appeal level reached,
      // not the assessor's proposed figure. A trend should show what the parcel is assessed at
      // now, not what was originally proposed. It therefore DELIBERATELY DIFFERS from a
      // `where appeal_level = 'proposed'` query: any parcel that has been through an appeal
      // contributes its later value here. If the two ever need to be reconciled, that is the
      // difference — not a bug.
      // The BASIS-following value is already resolved per parcel row by the shared scope, so the
      // toggle is honoured here without re-reading it. sumColumn returns null — not 0 — when no
      // parcel in the group carried a figure, and carries the estimate flag when any did.
      assessed: sumColumn(g.rows, 'final_value'),
      taxes: sumColumn(g.rows, 'taxes'),
      parcels: g.rows.length,
    }));

    // ── year-over-year, bridging gaps and naming what was bridged ─────────────────────────────
    const byProperty = new Map();
    groups.forEach((g) => {
      const list = byProperty.get(g.property_id) || [];
      list.push(g); byProperty.set(g.property_id, list);
    });

    const out = [];
    byProperty.forEach((list) => {
      list.sort((a, b) => a.tax_year.localeCompare(b.tax_year));
      list.forEach((g, i) => {
        // The most recent EARLIER year that actually has each figure. Searched independently for
        // value and tax: a property can have an unbroken value history and a gappy bill history,
        // and forcing them onto one comparison year would blank a change that is really there.
        const priorWith = (field) => {
          for (let j = i - 1; j >= 0; j--) if (toNumber(list[j][field]) != null) return list[j];
          return null;
        };
        const pv = priorWith('assessed');
        const pt = priorWith('taxes');

        const delta = (cur, prev, field, opts) => {
          const c = toNumber(cur?.[field]); const p = prev ? toNumber(prev[field]) : null;
          if (c == null || p == null) return { abs: null, pct: null };
          // A zero base is under every floor, so it reads n/m (with the reason) rather than a bare blank.
          return { abs: c - p, pct: percentChange(c, p, { ...opts, prevYear: prev.tax_year }) };
        };
        const dv = delta(g, pv, 'assessed', { floor: NM_MIN_ASSESSED, what: 'assessed', deltaCol: 'Δ Assessed', dp: 0 });
        const dt = delta(g, pt, 'taxes', { floor: NM_MIN_TAX, what: 'tax', deltaCol: 'Δ Taxes', dp: 2 });
        // An n/m cell carries its reason, not an estimate mark: the figure it would have marked is not shown.
        const pctCell = (d, cur, prev) => (typeof d.pct === 'number' ? flag(d.pct, cur, prev) : d.pct);

        // ⛔ NAME ONLY THE COMPARISONS THAT ACTUALLY HAPPENED. A prior year can exist while THIS
        // year has no figure to compare against it — a year with a status row and no notice yet.
        // Listing it anyway printed "vs 2022" beside four blank cells: a label asserting a
        // comparison that was never made, which is the one thing this column exists to prevent.
        // When value and tax bridged to DIFFERENT years, say both rather than pick one — an
        // ambiguous label is worse than a slightly long one.
        const vs = [...new Set([
          dv.abs != null ? pv?.tax_year : null,
          dt.abs != null ? pt?.tax_year : null,
        ].filter(Boolean))].sort();

        out.push({
          __key: `${g.property_id}|${g.tax_year}`,
          tax_year: g.tax_year, client_id: g.client_id, property: g.property, state: g.state,
          assessed: g.assessed, taxes: g.taxes, parcels: g.parcels,
          assessed_delta: flag(dv.abs, g.assessed, pv?.assessed),
          assessed_pct: pctCell(dv, g.assessed, pv?.assessed),
          taxes_delta: flag(dt.abs, g.taxes, pt?.taxes),
          taxes_pct: pctCell(dt, g.taxes, pt?.taxes),
          vs_year: vs.length ? vs.join(', ') : '',
          parcel_changes: i > 0 ? parcelChanges(list[i - 1].rows, g.rows, list[i - 1].tax_year, g.tax_year) : '',
        });
      });
    });

    return out.sort((a, b) =>
      String(a.state).localeCompare(String(b.state))
      || String(a.property).localeCompare(String(b.property))
      || String(a.tax_year).localeCompare(String(b.tax_year)));
  },

  // ⛔ THE PORTFOLIO TOTAL PER YEAR lives here rather than as extra table rows. A "Portfolio total"
  // row mixed into the data would be filtered by the property chips, sorted into the middle by a
  // header click, and double-counted by the totals footer. As a summary block it is computed from
  // the SAME filtered rows and cannot drift from them. The export carries it as its own sheet.
  summary(rows) {
    const years = [...new Set(rows.map((r) => String(r.tax_year)))].sort();
    return years.map((y) => {
      const yr = rows.filter((r) => String(r.tax_year) === y);
      const v = sumColumn(yr, 'assessed');
      const t = sumColumn(yr, 'taxes');
      const n = (x) => toNumber(x);
      return {
        label: y,
        value: n(v) == null ? '—' : `$${Math.round(n(v)).toLocaleString()}${isEstimated(v) ? '*' : ''}`,
        sub: n(t) == null ? 'no tax figure' : `tax $${n(t).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${isEstimated(t) ? '*' : ''}`,
      };
    });
  },

  exportSheets({ rows, columns, totals, footnotes, ctx }) {
    const client = ctx?.clientName || '';
    const years = [...new Set(rows.map((r) => String(r.tax_year)))].sort();
    const portfolio = years.map((y, i, arr) => {
      const yr = rows.filter((r) => String(r.tax_year) === y);
      const assessed = sumColumn(yr, 'assessed');
      const taxes = sumColumn(yr, 'taxes');
      const prevY = i > 0 ? arr[i - 1] : null;
      const prev = prevY ? rows.filter((r) => String(r.tax_year) === prevY) : null;
      const pa = prev ? toNumber(sumColumn(prev, 'assessed')) : null;
      const pt = prev ? toNumber(sumColumn(prev, 'taxes')) : null;
      const a = toNumber(assessed); const t = toNumber(taxes);
      return {
        year: y, assessed, taxes,
        assessed_delta: a != null && pa != null ? a - pa : null,
        taxes_delta: t != null && pt != null ? t - pt : null,
        // Which properties' parcel sets changed this year, so the year's total is not read as like-for-like.
        parcel_changes: yr.filter((r) => r.parcel_changes).map((r) => `${r.property}: ${r.parcel_changes}`).join(' | '),
      };
    });
    return [
      {
        name: 'Portfolio',
        titleRows: titleRows(client, 'Portfolio Tax Trend'),
        columns: [
          { key: 'year', label: 'Tax Year', type: 'year' },
          { key: 'assessed', label: 'Assessed Value', type: 'value' },
          { key: 'assessed_delta', label: 'Δ Assessed', type: 'value' },
          { key: 'taxes', label: 'Taxes', type: 'money' },
          { key: 'taxes_delta', label: 'Δ Taxes', type: 'money' },
          { key: 'parcel_changes', label: 'Parcel changes', type: 'text' },
        ],
        rows: portfolio,
        totals: { year: 'TOTAL', assessed: sumColumn(portfolio, 'assessed'), taxes: sumColumn(portfolio, 'taxes') },
        footnotes,
      },
      { name: 'By Property', titleRows: titleRows(client, 'Portfolio Tax Trend'), columns, rows, totals: totals || undefined, footnotes },
    ];
  },

  emptyMessage:
    'No property-years for this selection. Years before a property came into our representation are '
    + 'excluded, so a client whose portfolio starts later than the selected years will show nothing here.',
};

export default portfolioTaxTrend;
